import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

interface CrmConnectorSandboxDialogProps {
  open: boolean;
  accountName: string;
  currentBaseUrl?: string | null;
  currentDisplayKey?: string | null;
  isNativeContract?: boolean;
  onClose: () => void;
  onSave: (baseUrl: string, apiKey: string) => Promise<void>;
}

export default function CrmConnectorSandboxDialog({
  open,
  accountName,
  currentBaseUrl,
  currentDisplayKey,
  isNativeContract = true,
  onClose,
  onSave,
}: CrmConnectorSandboxDialogProps) {
  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setBaseUrl(currentBaseUrl ?? '');
      setApiKey('');
      setError(null);
      setSubmitting(false);
    }
  }, [open, currentBaseUrl]);

  const handleDialogClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick' || submitting) return;
    onClose();
  };

  const handleSave = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await onSave(baseUrl.trim(), apiKey.trim());
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save the sandbox credentials');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>Sandbox credentials</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={2} sx={{ pt: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Current key for {accountName}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
              color={currentDisplayKey ? 'text.primary' : 'text.secondary'}
            >
              {currentDisplayKey || 'Not set'}
            </Typography>
          </Box>

          <TextField
            label="Sandbox base URL"
            size="small"
            fullWidth
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            disabled={submitting}
            helperText={isNativeContract ? 'A trailing /v1 is dropped. Not verified when saved.' : 'Not verified when saved.'}
          />
          <TextField
            label="Sandbox API key"
            size="small"
            type="password"
            fullWidth
            autoComplete="new-password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            disabled={submitting}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={submitting || !baseUrl.trim() || !apiKey.trim()}
          sx={{ backgroundColor: '#465fff', '&:hover': { backgroundColor: '#3641f5' } }}
        >
          {submitting ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
